/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// @mui material components
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Pagination from "@mui/material/Pagination";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";
import TableContainer from "@mui/material/TableContainer";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import TableHeader from "components/Table/TableHeader";

// Material Dashboard 2 PRO React examples
import { useTranslation } from "react-i18next";
import { APP_STATE } from "services/Define";
import { FetchData } from "services/Request";
import { useSelector } from "react-redux";
import * as Func from "services/Function";
import CreditorOpen from "./CreditorOpen";
import CreditorPay from "./CreditorPay";

const initFind = {
  search: "",
  order_by: "tb.id",
  order_sort: "desc",
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

const stepperName = {
  edit: {
    name: "แก้ไขข้อมูล",
    color: "warning",
    icon: "border_color",
  },
  purchase: {
    name: "สั่งซื้อ",
    color: "info",
    icon: "task_alt",
  },
  receive: {
    name: "รับบางส่วน",
    color: "info",
    icon: "task_alt",
  },
  success: {
    name: "ได้รับครบ",
    color: "success",
    icon: "task_alt",
  },
  reject: {
    name: "ยกเลิก",
    color: "error",
    icon: "cancel",
  },
};

function CreditorPO() {
  const mod = "creditor_po";
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: "tb.status", label: t(`attr.enable`), align: "center" },
    { id: "tb.code", label: t(`attr.code`), align: "center" },
    { id: "tb.type_id", label: "ประเภท", align: "left" },
    { id: "tb.date_record", label: "วันสั่งซื้อ", align: "center" },
    { id: "tb.remark", label: "กำหนดชำระ", align: "center" },
    { id: "tb.user_id", label: "จำนวนเงิน", align: "right" },
    { id: "tb.paid", label: "ชำระแล้ว", align: "right" },
    { id: "tb.balance", label: "ค้างชำระ", align: "right" },
    { id: "tb.created_at", label: "ชำระเงิน", align: "center" },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData("POST", `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      ref_id: id,
    }).then((result) => {
      if (result.status === "ok") {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  };

  const statusDiaplay = (item) => {
    return (
      <Alert
        icon={<Icon>{stepperName[item.status].icon}</Icon>}
        severity={stepperName[item.status].color}
        sx={{
          fontSize: "14px",
          padding: "0 10px",
          fontWeight: 400,
          borderRadius: 5,
          whiteSpace: "nowrap",
        }}
      >
        {stepperName[item.status].name}
      </Alert>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? "desc" : "asc" });
  };

  const init = () => {
    FetchData("GET", `/${mod}`, app.token, {
      ref_id: id,
    }).then((result) => {
      if (result.status === "ok") {
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDBox
              noValidate
              component="form"
              role="form"
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, page: 1 });
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <MDInput
                    name="searchform"
                    fullWidth
                    value={find.search}
                    onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">{t("element.search")} :</InputAdornment>
                      ),
                      autoComplete: "off",
                    }}
                  />
                </Grid>
              </Grid>
            </MDBox>
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0 }}>
              <Table aria-label="simple table">
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell align="center" width={160}>
                        {statusDiaplay(row)}
                      </TableCell>
                      <TableCell align="center">
                        <CreditorOpen parent={row} />
                      </TableCell>
                      <TableCell align="left">{row.type_name}</TableCell>
                      <TableCell align="center">{Func.dateDisplay(row.date_record)}</TableCell>
                      <TableCell align="center">{Func.dateDisplay(row.date_deadline)}</TableCell>
                      <TableCell align="right" className="color-info">
                        {Func.numberDisplay(row.nettotal)}
                      </TableCell>
                      <TableCell align="right" className="color-success">
                        {Func.numberDisplay(row.paid)}
                      </TableCell>
                      <TableCell align="right" className="color-danger">
                        {Func.numberDisplay(row.balance)}
                      </TableCell>
                      <TableCell align="center">
                        <CreditorPay parent={row} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
              <MDButton variant={show ? "contained" : "outlined"} color="dark" onClick={openShow}>
                {t(`element.show_record`, { num: find.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderShow}
              <Pagination
                count={pageTotal}
                variant="outlined"
                color="secondary"
                showFirstButton
                showLastButton
                page={pageNo}
                onChange={(e, p) => view({ ...find, page: p })}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <div>
      {renderList()}
      {renderLoading()}
    </div>
  );
}

export default CreditorPO;
